import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Edit2, ExternalLink, Plus, Trash2, UserPlus } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { SectionTitle } from '@/components/design-system';
import { FormDialog } from '@/components/forms/FormDialog';
import { FormField, FormSelect, FormTextarea } from '@/components/forms/FormField';
import {
  useConvertLead,
  useDeleteLead,
  useLeadsMarketing,
  useUpsertLead,
  type UpsertLeadInput,
} from '@/hooks/queries/useLeadsMarketing';
import { useSessions } from '@/hooks/queries/useFormation';
import { useUsersList } from '@/hooks/queries/useUsersList';
import type {
  CampagneBoostRow,
  LeadMarketingRow,
  LeadStatut,
  PageMarketingRow,
} from '@/lib/database.types';
import { formatDA, formatDateDZ } from '@/lib/format';

const STATUTS: LeadStatut[] = ['nouveau', 'repondu', 'interesse', 'a_relancer', 'inscrit', 'perdu'];

const STATUT_LABEL: Record<LeadStatut, string> = {
  nouveau: 'Nouveau',
  repondu: 'Répondu',
  interesse: 'Intéressé',
  a_relancer: 'À relancer',
  inscrit: 'Inscrit',
  perdu: 'Perdu',
};

const STATUT_VARIANT: Record<
  LeadStatut,
  'default' | 'secondary' | 'success' | 'destructive' | 'outline'
> = {
  nouveau: 'outline',
  repondu: 'secondary',
  interesse: 'default',
  a_relancer: 'destructive',
  inscrit: 'success',
  perdu: 'outline',
};

interface Props {
  entrepriseId: string;
  pages: PageMarketingRow[];
  campagnes: CampagneBoostRow[];
}

export function LeadsTab({ entrepriseId, pages, campagnes }: Props): JSX.Element {
  const [filterStatut, setFilterStatut] = useState<LeadStatut | ''>('');
  const { data: leads = [] } = useLeadsMarketing({
    entrepriseId,
    statut: filterStatut || null,
  });
  const { data: users = [] } = useUsersList();
  const upsert = useUpsertLead();
  const del = useDeleteLead();

  const [editing, setEditing] = useState<LeadMarketingRow | null>(null);
  const [open, setOpen] = useState(false);
  const [confirmDel, setConfirmDel] = useState<LeadMarketingRow | null>(null);
  const [converting, setConverting] = useState<LeadMarketingRow | null>(null);

  const userMap = useMemo(() => {
    const m = new Map<string, string>();
    for (const u of users) m.set(u.user_id, u.full_name ?? u.user_id.slice(0, 8));
    return m;
  }, [users]);
  const pageMap = useMemo(() => {
    const m = new Map<string, string>();
    for (const p of pages) m.set(p.id, `${p.plateforme} · ${p.nom}`);
    return m;
  }, [pages]);
  const campagneMap = useMemo(() => {
    const m = new Map<string, CampagneBoostRow>();
    for (const c of campagnes) m.set(c.id, c);
    return m;
  }, [campagnes]);

  const counts = useMemo(() => {
    const c = { nouveau: 0, repondu: 0, interesse: 0, a_relancer: 0, inscrit: 0, perdu: 0 };
    for (const l of leads) c[l.statut] = (c[l.statut] ?? 0) + 1;
    return c;
  }, [leads]);

  return (
    <>
      <SectionTitle
        trailing={
          <Button
            size="sm"
            onClick={() => {
              setEditing(null);
              setOpen(true);
            }}
          >
            <Plus className="h-3.5 w-3.5" /> Nouveau lead
          </Button>
        }
      >
        Leads B2C — pipeline conversion
      </SectionTitle>

      {/* Mini-funnel : compteurs cliquables */}
      <div className="mt-3 flex flex-wrap gap-1">
        <button
          type="button"
          onClick={() => setFilterStatut('')}
          className={
            filterStatut === ''
              ? 'rounded-md bg-rose-100 px-2.5 py-1 text-[11px] font-semibold text-rose-800'
              : 'rounded-md bg-slate-100 px-2.5 py-1 text-[11px] text-slate-700 hover:bg-slate-200'
          }
        >
          Tous ({leads.length})
        </button>
        {STATUTS.map((s) => (
          <button
            key={s}
            type="button"
            onClick={() => setFilterStatut(s === filterStatut ? '' : s)}
            className={
              filterStatut === s
                ? 'rounded-md bg-rose-100 px-2.5 py-1 text-[11px] font-semibold text-rose-800'
                : 'rounded-md bg-slate-100 px-2.5 py-1 text-[11px] text-slate-700 hover:bg-slate-200'
            }
          >
            {STATUT_LABEL[s]} ({counts[s] ?? 0})
          </button>
        ))}
      </div>

      {leads.length === 0 ? (
        <p className="mt-4 text-sm text-muted-foreground">
          Aucun lead{filterStatut ? ` au statut "${STATUT_LABEL[filterStatut]}"` : ''}. Crée le
          premier prospect capté via une page FB/TikTok.
        </p>
      ) : (
        <div className="mt-3 overflow-hidden rounded-xl border border-slate-200 bg-white">
          <table className="w-full text-xs">
            <thead className="bg-slate-50 text-[10px] font-bold uppercase text-slate-600">
              <tr>
                <th className="px-3 py-2 text-left">Prospect</th>
                <th className="px-3 py-2 text-left">Téléphone</th>
                <th className="px-3 py-2 text-left">Page</th>
                <th className="px-3 py-2 text-left">Campagne</th>
                <th className="px-3 py-2 text-left">Assigné</th>
                <th className="px-3 py-2">Statut</th>
                <th className="px-3 py-2 text-right">Reçu le</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {leads.map((l) => (
                <tr key={l.id} className="border-t border-slate-100">
                  <td className="px-3 py-2 font-medium">{l.nom_prospect ?? '— anonyme —'}</td>
                  <td className="px-3 py-2 font-mono text-[11px]">{l.telephone ?? '—'}</td>
                  <td className="px-3 py-2 text-slate-600">
                    {l.source_page_id ? (pageMap.get(l.source_page_id) ?? '—') : '—'}
                  </td>
                  <td className="px-3 py-2 font-mono text-[10px] text-slate-500">
                    {l.campagne_id ? (campagneMap.get(l.campagne_id)?.reference ?? '—') : '—'}
                  </td>
                  <td className="px-3 py-2 text-slate-600">
                    {l.assigned_to_user_id ? (userMap.get(l.assigned_to_user_id) ?? '—') : '—'}
                  </td>
                  <td className="px-3 py-2 text-center">
                    <Badge variant={STATUT_VARIANT[l.statut]} className="text-[10px]">
                      {STATUT_LABEL[l.statut]}
                    </Badge>
                  </td>
                  <td className="px-3 py-2 text-right text-[10px] text-slate-500">
                    {formatDateDZ(l.created_at.slice(0, 10))}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <div className="flex items-center justify-end gap-1">
                      {l.statut === 'inscrit' && l.inscription_id ? (
                        <Link
                          to="/formation/inscriptions"
                          className="inline-flex items-center gap-0.5 rounded bg-emerald-50 px-1.5 py-0.5 text-[10px] font-semibold text-emerald-700 hover:bg-emerald-100"
                          title="Inscription liée"
                        >
                          <ExternalLink className="h-2.5 w-2.5" /> Insc
                        </Link>
                      ) : (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7 text-emerald-600 hover:bg-emerald-50"
                          onClick={() => setConverting(l)}
                          title="Convertir en inscription"
                        >
                          <UserPlus className="h-3 w-3" />
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7"
                        onClick={() => {
                          setEditing(l);
                          setOpen(true);
                        }}
                      >
                        <Edit2 className="h-3 w-3" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 text-rose-600"
                        onClick={() => setConfirmDel(l)}
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <LeadFormDialog
        open={open}
        onOpenChange={(o) => {
          if (!o) {
            setOpen(false);
            setEditing(null);
          }
        }}
        editing={editing}
        entrepriseId={entrepriseId}
        pages={pages}
        campagnes={campagnes}
        users={users.map((u) => ({ id: u.user_id, name: u.full_name ?? u.user_id.slice(0, 8) }))}
        onSubmit={async (input) => {
          await upsert.mutateAsync(input);
          toast.success(editing ? 'Lead mis à jour' : 'Lead créé');
          setOpen(false);
          setEditing(null);
        }}
        isSubmitting={upsert.isPending}
      />

      <ConfirmDialog
        open={!!confirmDel}
        onOpenChange={(o) => !o && setConfirmDel(null)}
        destructive
        title={confirmDel ? `Supprimer le lead ${confirmDel.nom_prospect ?? ''} ?` : ''}
        description="Cette action est irréversible."
        loading={del.isPending}
        onConfirm={async () => {
          if (!confirmDel) return;
          await del.mutateAsync(confirmDel.id);
          toast.success('Lead supprimé');
          setConfirmDel(null);
        }}
      />

      <LeadConvertDialog
        open={!!converting}
        onOpenChange={(o) => !o && setConverting(null)}
        lead={converting}
        campagnes={campagnes}
        onDone={() => setConverting(null)}
      />
    </>
  );
}

// ===== LeadFormDialog =====

function LeadFormDialog({
  open,
  onOpenChange,
  editing,
  entrepriseId,
  pages,
  campagnes,
  users,
  onSubmit,
  isSubmitting,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  editing: LeadMarketingRow | null;
  entrepriseId: string;
  pages: PageMarketingRow[];
  campagnes: CampagneBoostRow[];
  users: { id: string; name: string }[];
  onSubmit: (input: UpsertLeadInput) => Promise<void>;
  isSubmitting: boolean;
}): JSX.Element {
  const [nom, setNom] = useState('');
  const [telephone, setTelephone] = useState('');
  const [pageId, setPageId] = useState('');
  const [campagneId, setCampagneId] = useState('');
  const [assignedTo, setAssignedTo] = useState('');
  const [statut, setStatut] = useState<LeadStatut>('nouveau');
  const [notes, setNotes] = useState('');

  useEffect(() => {
    if (open) {
      setNom(editing?.nom_prospect ?? '');
      setTelephone(editing?.telephone ?? '');
      setPageId(editing?.source_page_id ?? '');
      setCampagneId(editing?.campagne_id ?? '');
      setAssignedTo(editing?.assigned_to_user_id ?? '');
      setStatut(editing?.statut ?? 'nouveau');
      setNotes(editing?.notes ?? '');
    }
  }, [open, editing]);

  const handle = async (e: React.FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    await onSubmit({
      id: editing?.id,
      entreprise_id: entrepriseId,
      nom_prospect: nom.trim() || null,
      telephone: telephone.trim() || null,
      source_page_id: pageId || null,
      campagne_id: campagneId || null,
      assigned_to_user_id: assignedTo || null,
      statut,
      notes: notes.trim() || null,
    });
  };

  return (
    <FormDialog
      open={open}
      onOpenChange={onOpenChange}
      title={editing ? 'Modifier le lead' : 'Nouveau lead'}
      onSubmit={handle}
      isSubmitting={isSubmitting}
      submitLabel={editing ? 'Enregistrer' : 'Créer'}
      size="md"
    >
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <FormField
          label="Nom du prospect"
          value={nom}
          onChange={(e) => setNom(e.target.value)}
          hint="Optionnel — laisser vide si anonyme"
        />
        <FormField
          label="Téléphone"
          value={telephone}
          onChange={(e) => setTelephone(e.target.value)}
        />
        <FormSelect
          label="Page source"
          value={pageId}
          onChange={setPageId}
          options={[
            { value: '', label: '— Aucune —' },
            ...pages.map((p) => ({ value: p.id, label: `${p.plateforme} · ${p.nom}` })),
          ]}
        />
        <FormSelect
          label="Campagne"
          value={campagneId}
          onChange={setCampagneId}
          options={[
            { value: '', label: '— Aucune —' },
            ...campagnes.map((c) => ({ value: c.id, label: c.reference })),
          ]}
          hint="Lien vers la campagne boost qui a généré le lead"
        />
        <FormSelect
          label="Assignée à"
          value={assignedTo}
          onChange={setAssignedTo}
          options={[
            { value: '', label: '— Personne —' },
            ...users.map((u) => ({ value: u.id, label: u.name })),
          ]}
          hint="Commerciale qui suit ce lead"
        />
        <FormSelect
          label="Statut"
          value={statut}
          onChange={(v) => setStatut(v as LeadStatut)}
          options={STATUTS.map((s) => ({ value: s, label: STATUT_LABEL[s] }))}
        />
        <FormTextarea
          label="Notes / dernier échange"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={3}
          fullWidth
        />
      </div>
    </FormDialog>
  );
}

// ===== LeadConvertDialog =====

const PRIX_DEFAULT = 25000;
const STRATEGIX_DEFAULT = 10000;
const PRIME_DEFAULT = 1000;

function LeadConvertDialog({
  open,
  onOpenChange,
  lead,
  campagnes,
  onDone,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  lead: LeadMarketingRow | null;
  campagnes: CampagneBoostRow[];
  onDone: () => void;
}): JSX.Element {
  const formationId = lead?.campagne_id
    ? (campagnes.find((c) => c.id === lead.campagne_id)?.formation_id ?? null)
    : null;

  const { data: sessions = [] } = useSessions({ formationId });
  const convert = useConvertLead();

  const [tierNom, setTierNom] = useState('');
  const [sessionId, setSessionId] = useState('');
  const [prix, setPrix] = useState(String(PRIX_DEFAULT));
  const [strategix, setStrategix] = useState(String(STRATEGIX_DEFAULT));
  const [prime, setPrime] = useState(String(PRIME_DEFAULT));
  const [acompte, setAcompte] = useState(String(STRATEGIX_DEFAULT));
  const [acompteUrl, setAcompteUrl] = useState('');
  const [paiementComplete, setPaiementComplete] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open && lead) {
      setTierNom(lead.nom_prospect ?? '');
      setSessionId('');
      setPrix(String(PRIX_DEFAULT));
      setStrategix(String(STRATEGIX_DEFAULT));
      setPrime(String(PRIME_DEFAULT));
      setAcompte(String(STRATEGIX_DEFAULT));
      setAcompteUrl('');
      setPaiementComplete(false);
      setError(null);
    }
  }, [open, lead]);

  const margeKost = Number(prix) - Number(strategix) - Number(prime);

  const handle = async (e: React.FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    if (!lead) return;
    if (!tierNom.trim()) return setError('Nom du candidat obligatoire pour créer le tier');
    if (!sessionId) return setError('Sélectionne la session formation');
    if (!(Number(prix) > 0)) return setError('Prix invalide');
    try {
      await convert.mutateAsync({
        lead,
        session_id: sessionId,
        prix_facture: Number(prix),
        revenu_strategix_dzd: Number(strategix),
        prime_montant: Number(prime),
        montant_acompte: Number(acompte),
        acompte_ccp_url: acompteUrl.trim() || null,
        paiement_complete: paiementComplete,
        tier_raison_sociale: tierNom.trim(),
        tier_telephone: lead.telephone,
      });
      toast.success(`Lead converti en inscription · marge KOST ${formatDA(margeKost)}`);
      onDone();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur');
    }
  };

  if (!lead) {
    return (
      <FormDialog
        open={open}
        onOpenChange={onOpenChange}
        title="Convertir le lead"
        onSubmit={(e) => e.preventDefault()}
        submitLabel="Fermer"
        size="md"
      >
        <span />
      </FormDialog>
    );
  }

  return (
    <FormDialog
      open={open}
      onOpenChange={onOpenChange}
      title="Convertir le lead en inscription"
      description={`Lead "${lead.nom_prospect ?? lead.telephone ?? lead.id.slice(0, 8)}" → inscription B2C`}
      onSubmit={handle}
      isSubmitting={convert.isPending}
      submitLabel="Confirmer la conversion"
      size="lg"
    >
      {error && (
        <div className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive">
          {error}
        </div>
      )}
      {!formationId && (
        <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900">
          Ce lead n&apos;est rattaché à aucune campagne — la liste de sessions est vide. Édite le
          lead d&apos;abord pour préciser sa campagne, ou choisis la session manuellement.
        </div>
      )}

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <FormField
          label="Nom du candidat (devient un tier client_b2c)"
          value={tierNom}
          onChange={(e) => setTierNom(e.target.value)}
          required
          fullWidth
          hint={
            lead.tier_id
              ? 'Tier déjà existant — sera réutilisé'
              : 'Un nouveau tier client_b2c sera créé'
          }
        />
        <FormSelect
          label="Session formation"
          value={sessionId}
          onChange={setSessionId}
          options={[
            { value: '', label: '— Sélectionner —' },
            ...sessions.map((s) => ({
              value: s.id,
              label: `${s.reference} · ${formatDateDZ(s.date_debut)}`,
            })),
          ]}
          required
        />
        <FormField
          label="Prix candidat"
          type="number"
          step="100"
          min="0"
          value={prix}
          onChange={(e) => setPrix(e.target.value)}
          required
          hint="25 000 par défaut B2C"
        />
        <FormField
          label="Part Strategix"
          type="number"
          step="100"
          min="0"
          value={strategix}
          onChange={(e) => setStrategix(e.target.value)}
          hint="Revenu certificat (poche perso)"
        />
        <FormField
          label="Prime commerciale"
          type="number"
          step="100"
          min="0"
          value={prime}
          onChange={(e) => setPrime(e.target.value)}
          hint={
            lead.assigned_to_user_id
              ? 'Versée à la fille assignée'
              : 'Personne assignée — pas de prime'
          }
        />
        <FormField
          label="Acompte versé (CCP)"
          type="number"
          step="100"
          min="0"
          value={acompte}
          onChange={(e) => setAcompte(e.target.value)}
          required
          hint="Acompte avant la formation"
        />
        <FormField
          label="URL preuve CCP (optionnel)"
          value={acompteUrl}
          onChange={(e) => setAcompteUrl(e.target.value)}
          fullWidth
          hint="Lien vers reçu/photo du versement"
        />
        <label className="flex items-center gap-2 text-xs sm:col-span-2">
          <input
            type="checkbox"
            checked={paiementComplete}
            onChange={(e) => setPaiementComplete(e.target.checked)}
            className="h-3.5 w-3.5"
          />
          <span>
            Paiement complet (sinon le certificat sera bloqué tant que solde n&apos;est pas réglé)
          </span>
        </label>
      </div>

      <div className="rounded-md border border-emerald-200 bg-emerald-50/60 p-3 text-xs">
        <div className="flex items-center justify-between gap-3">
          <span className="font-semibold text-emerald-900">Récap décomposition</span>
          <span className={`font-bold ${margeKost >= 0 ? 'text-emerald-700' : 'text-rose-700'}`}>
            Marge KOST (avant formateur+matériel) : {formatDA(margeKost)}
          </span>
        </div>
        <div className="mt-1 grid grid-cols-3 gap-2 text-[11px]">
          <span>
            Prix : <strong>{formatDA(Number(prix))}</strong>
          </span>
          <span className="text-rose-700">− Strategix : {formatDA(Number(strategix))}</span>
          <span className="text-rose-700">− Prime : {formatDA(Number(prime))}</span>
        </div>
      </div>
    </FormDialog>
  );
}
